import { Theme } from '@mui/material/styles';
import { darken } from '@mui/material/styles';

// Import the types from our central definition file
import '../types/theme.d';

/**
 * Helper function to get theme-aware colors for various states
 */
export const getThemeStateColors = (theme: Theme) => {
  // Extract primary color and possible overrides from button component
  const primaryColor = theme.palette.primary.main;

  // Try to get button-specific color overrides if available
  const buttonRoot = theme.components?.MuiButton?.styleOverrides?.root as any;
  const buttonContained = theme.components?.MuiButton?.styleOverrides
    ?.contained as any;

  // Default color is primary.main, but could be overridden by button styles
  const buttonBgColor = buttonRoot?.backgroundColor || primaryColor;
  const buttonTextColor =
    theme.custom?.buttonTextColor ||
    buttonRoot?.color ||
    theme.palette.getContrastText(primaryColor);

  // Get hover color from overrides or fall back to darkened primary
  const getHoverColor = () => {
    // First check if there's an explicit hover color in the styleOverrides
    if (buttonRoot?.['&:hover']?.backgroundColor) {
      return buttonRoot['&:hover'].backgroundColor;
    }

    // Then check if there's a contained button override
    if (buttonContained?.['&:hover']?.backgroundColor) {
      return buttonContained['&:hover'].backgroundColor;
    }

    // Fall back to a darkened version of whatever the button color is
    return darken(buttonBgColor, 0.1);
  };

  // Get active color from overrides or fall back to more darkened primary
  const getActiveColor = () => {
    if (buttonRoot?.['&:active']?.backgroundColor) {
      return buttonRoot['&:active'].backgroundColor;
    }
    return darken(buttonBgColor, 0.2);
  };

  return {
    // Button colors
    button: {
      default: buttonBgColor,
      hover: getHoverColor(),
      active: getActiveColor(),
      disabled: {
        background: theme.palette.action.disabledBackground || '#E5E6E7',
        text: theme.palette.action.disabled || '#C7CACB',
      },
      text: buttonTextColor,
    },
    // Input colors
    input: {
      border: {
        default: theme.palette.grey[300] || '#C7CACB',
        hover: theme.palette.text.primary,
        focus: theme.palette.primary.main,
        error: theme.palette.error.main,
        disabled: theme.palette.grey[300] || '#E0E0E0',
      },
      label: {
        default: theme.palette.grey[600] || '#70787C',
        focus: theme.palette.primary.main,
      },
    },
    // Background opacity values for hover states
    opacity: {
      hover: 0.04,
      active: 0.08,
      focus: 0.12,
    },
  };
};

/**
 * Helper function to create rgba from a hex color (for hover states)
 */
export const hexToRgba = (hex: string, opacity: number): string => {
  // Remove # if present
  hex = hex.replace('#', '');

  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Creates a hover background color with specified opacity from any color
 * @param color The base color (hex, rgb, or named color)
 * @param opacity The opacity to apply (0-1)
 * @returns A rgba color string for hover states
 */
export const createHoverBackground = (
  color: string,
  opacity: number = 0.04
): string => {
  // Default fallback for non-hex colors
  if (!color.startsWith('#')) {
    return `rgba(255, 0, 0, ${opacity})`; // Default using Axle red with opacity
  }

  try {
    // Convert hex to rgba
    return hexToRgba(color, opacity);
  } catch (e) {
    // Fallback if parsing fails
    return `rgba(0, 0, 0, ${opacity})`;
  }
};

/**
 * Creates a darker version of a color for button hover states
 * @param color The base color (hex format preferred)
 * @param factor The darkening factor (0-1, where lower = darker)
 * @returns A darkened version of the color
 */
export const createDarkenedColor = (
  color: string,
  factor: number = 0.9
): string => {
  // Default fallback for non-hex colors
  if (!color.startsWith('#')) {
    return darken(color, 0.1); // Use MUI's darken for non-hex
  }

  try {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darken by reducing RGB values
    const newR = Math.max(0, Math.floor(r * factor));
    const newG = Math.max(0, Math.floor(g * factor));
    const newB = Math.max(0, Math.floor(b * factor));

    // Convert back to hex
    return `#${newR.toString(16).padStart(2, '0')}${newG
      .toString(16)
      .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  } catch (e) {
    // Fallback if parsing fails
    return darken(color, 0.1);
  }
};

/**
 * ESLint custom rule to detect hardcoded colors
 * This is for documentation only and needs to be implemented in .eslintrc
 */
export const ESLINT_COLOR_RULE = `
// Add to .eslintrc.js
{
  "rules": {
    "no-hardcoded-colors": {
      "create": function(context) {
        return {
          Literal(node) {
            if (typeof node.value === "string" && node.value.match(/^#[0-9A-Fa-f]{3,6}$/)) {
              context.report({
                node,
                message: "Avoid hardcoded color values. Use theme.palette instead."
              });
            }
          }
        };
      }
    }
  }
}
`;

/**
 * List of components that should use theme properties
 * This is for documentation and reference
 */
export const THEME_AWARE_COMPONENTS = [
  'MuiButton',
  'MuiIconButton',
  'MuiOutlinedInput',
  'MuiInputLabel',
  'MuiCheckbox',
  'MuiRadio',
  'MuiSwitch',
  'MuiSelect',
  'MuiPaper',
  'MuiCard',
  'MuiAppBar',
  'MuiToolbar',
  'MuiListItem',
  'MuiTab',
  'MuiChip',
  'MuiAvatar',
  'MuiTooltip',
];
